import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import YardIcon from '@mui/icons-material/Yard';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EventNoteIcon from '@mui/icons-material/EventNote';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { BOTTOM_NAV_HEIGHT, useBottomNavVisible } from './useBottomNavVisible';

export interface BottomNavTarget {
  value: string;
  label: string;
  path: string;
  icon: React.ReactElement;
}

export const BOTTOM_NAV_TARGETS: BottomNavTarget[] = [
  { value: 'gardens', label: 'Kertjeim', path: '/gardens', icon: <YardIcon /> },
  { value: 'plants', label: 'Növények', path: '/plants', icon: <LocalFloristIcon /> },
  { value: 'plant-lists', label: 'Listáim', path: '/plant-lists', icon: <ListAltIcon /> },
  { value: 'garden-calendar', label: 'Naptár', path: '/garden-calendar', icon: <EventNoteIcon /> },
];

export const MORE_VALUE = 'more';

/** Az aktív cél az útvonal első szegmense alapján (pl. /gardens/123 → Kertjeim). */
export const getActiveBottomNavValue = (pathname: string): string | null => {
  const firstSegment = pathname.split('/').filter(Boolean)[0] ?? '';
  const match = BOTTOM_NAV_TARGETS.find((target) => target.value === firstSegment);
  return match ? match.value : null;
};

interface MobileBottomNavProps {
  /** A „Több” gomb – az oldalsó menüt (Drawer) nyitja. */
  onMore: () => void;
  moreOpen?: boolean;
}

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ onMore, moreOpen = false }) => {
  const visible = useBottomNavVisible();
  const location = useLocation();
  const navigate = useNavigate();

  if (!visible) return null;

  const activeValue = moreOpen ? MORE_VALUE : getActiveBottomNavValue(location.pathname);

  const handleChange = (_event: React.SyntheticEvent, value: string) => {
    if (value === MORE_VALUE) {
      onMore();
      return;
    }
    const target = BOTTOM_NAV_TARGETS.find((t) => t.value === value);
    if (target) navigate(target.path);
  };

  return (
    <Paper
      component="nav"
      aria-label="Gyors navigáció"
      elevation={8}
      data-testid="mobile-bottom-nav"
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        pb: 'env(safe-area-inset-bottom)',
        pl: 'env(safe-area-inset-left)',
        pr: 'env(safe-area-inset-right)',
        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
      }}
    >
      <BottomNavigation
        showLabels
        value={activeValue}
        onChange={handleChange}
        sx={{ height: BOTTOM_NAV_HEIGHT }}
      >
        {BOTTOM_NAV_TARGETS.map((target) => (
          <BottomNavigationAction
            key={target.value}
            value={target.value}
            label={target.label}
            icon={target.icon}
            sx={{ minWidth: 0, minHeight: 44, flex: 1, px: 0.5 }}
          />
        ))}
        <BottomNavigationAction
          value={MORE_VALUE}
          label="Több"
          icon={<MoreHorizIcon />}
          aria-haspopup="true"
          sx={{ minWidth: 0, minHeight: 44, flex: 1, px: 0.5 }}
        />
      </BottomNavigation>
    </Paper>
  );
};

export default MobileBottomNav;
